import { buildAdminUserSenderRunPath, fetchAdminUserSenderRuns } from '@/api/admin';
import Pressable from '@/components/motion/pressable';
import { Spinner } from '@/components/ui/spinner';
import type { RunSummary } from '@/entities/sender.entity';
import LandingsEmptyState from '@/pages/landings/components/landings-empty-state';
import LandingsErrorState from '@/pages/landings/components/landings-error-state';
import { RUN_STATUS_LABELS, TERMINAL_STATUSES } from '@/pages/sender/sender-run-detail.constants';
import { cn } from '@/lib/utils';
import { ChevronRightIcon, SendIcon } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

const POLL_INTERVAL_MS = 4000;

function truncateCaption(text: string, max = 80): string {
	if (text.length <= max) return text;
	return `${text.slice(0, max - 1)}…`;
}

interface SenderRunsSubtabProps {
	profileId: number;
	buildRunPath?: (profileId: number, runId: string) => string;
	fetchRuns?: typeof fetchAdminUserSenderRuns;
}

export default function SenderRunsSubtab({
	profileId,
	buildRunPath = buildAdminUserSenderRunPath,
	fetchRuns = fetchAdminUserSenderRuns,
}: SenderRunsSubtabProps) {
	const navigate = useNavigate();
	const [runs, setRuns] = useState<RunSummary[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const runsRef = useRef<RunSummary[]>([]);
	runsRef.current = runs;

	const load = useCallback(async (showSpinner = false) => {
		if (showSpinner) {
			setLoading(true);
			setError(false);
		}
		try {
			const data = await fetchRuns(profileId, { limit: 50 });
			setRuns(data.items);
			setError(false);
		} catch {
			if (showSpinner || runsRef.current.length === 0) {
				setError(true);
			}
		} finally {
			if (showSpinner) setLoading(false);
		}
	}, [profileId, fetchRuns]);

	useEffect(() => {
		void load(true);
	}, [load]);

	useEffect(() => {
		const hasActive = runs.some(run => !TERMINAL_STATUSES.has(run.status));
		if (!hasActive) return;

		const intervalId = window.setInterval(() => {
			void load();
		}, POLL_INTERVAL_MS);

		return () => window.clearInterval(intervalId);
	}, [runs, load]);

	if (loading) {
		return (
			<div className='flex justify-center py-8'>
				<Spinner />
			</div>
		);
	}

	if (error && runs.length === 0) {
		return (
			<LandingsErrorState
				message='Не удалось загрузить запуски sender. Проверьте telegram-sender и SENDER_INTERNAL_SECRET.'
				onRetry={() => void load(true)}
			/>
		);
	}

	if (runs.length === 0) {
		return (
			<LandingsEmptyState
				icon={SendIcon}
				title='Запусков нет'
				description='У пользователя пока нет sender-запусков.'
			/>
		);
	}

	return (
		<div className='flex flex-col gap-2'>
			{runs.map(run => (
				<Pressable
					key={run.id}
					className='cursor-pointer rounded-xl border border-border bg-card px-3 py-3 text-sm'
					onClick={() => navigate(buildRunPath(profileId, run.id))}
				>
					<div className='flex items-start justify-between gap-2'>
						<div className='min-w-0 flex-1'>
							<p className='font-medium truncate'>{truncateCaption(run.caption)}</p>
							<p className='text-xs text-muted-foreground truncate mt-1'>
								<span
									className={cn(
										run.status === 'failed' && 'text-destructive',
										run.status === 'running' && 'text-chart-4',
										(run.status === 'completed' || run.status === 'completed_empty') && 'text-chart-2',
									)}
								>
									{RUN_STATUS_LABELS[run.status] ?? run.status}
								</span>
								{run.runSource ? ` · ${run.runSource}` : ''}
								{run.authJobId ? ` · job ${run.authJobId.slice(0, 8)}` : ''}
								{run.linkId ? ` · ${run.linkId}` : ''}
								{run.landingHostname ? ` · ${run.landingHostname}` : ''}
								{run.sessionLabel ? ` · ${run.sessionLabel}` : ''}
								{(run.deliveredCount ?? 0) > 0 ? ` · доставлено ${run.deliveredCount}` : ''}
							</p>
							<p className='text-xs text-muted-foreground mt-1 font-mono truncate'>{run.id}</p>
						</div>
						<ChevronRightIcon size={16} className='text-muted-foreground shrink-0 mt-0.5' />
					</div>
				</Pressable>
			))}
		</div>
	);
}
